/**
 * PHARMAKON — exhaustive list pagination for "load-all" fetches (dropdowns).
 *
 * R3-H28 — the fournisseur dropdown asked the server for limit=500, but every
 * list validator caps `query.limit` at LIST_LIMIT_MAX (422 "Limite max : 200"),
 * so the supplier pickers on /achats, /commercial/catalogue and
 * /commercial/achat-urgent silently stayed empty. This helper walks the
 * paginated list endpoint WITHIN the cap and aggregates the rows.
 *
 * Pure + injectable (the page fetcher is a parameter) so it is testable with
 * an in-memory stub and reusable by any other "load-all" dropdown.
 */
import { LIST_LIMIT_MAX, DROPDOWN_MAX_PAGES } from "../config/app.config.js";

/**
 * @param {(params: { page: number, limit: number }) => Promise<{ data?: unknown[], total?: number }>} fetchPage
 *        One page of the list endpoint — receives { page, limit } and resolves
 *        the unwrapped `{ data, total }` envelope (api.client returns res.data).
 * @param {{ limit?: number, maxPages?: number }} [opts]
 * @returns {Promise<unknown[]>} every row, in server order.
 */
export async function fetchAllPages(fetchPage, opts = {}) {
  const limit = opts.limit ?? LIST_LIMIT_MAX;
  const maxPages = opts.maxPages ?? DROPDOWN_MAX_PAGES;

  const all = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const r = await fetchPage({ page, limit });
    const rows = Array.isArray(r?.data) ? r.data : [];
    all.push(...rows);

    // Stop on the last page: a short page always ends the walk; when the
    // envelope carries a numeric total, honour it too (defensive on both).
    const total = typeof r?.total === "number" ? r.total : null;
    if (rows.length < limit) break;
    if (total !== null && all.length >= total) break;
  }
  return all;
}

export default fetchAllPages;
